import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import type { AttachmentRef, DocumentLink, VaultItem } from '@heybesafe/shared';
import { v4 as uuidv4 } from 'uuid';
import { useSafe } from '../../context/SafeContext';
import { useFamilyCategories } from '../../hooks/useFamilyCategories';
import { shareItemWithFamily } from '../../api/client';
import { base64UrlToBytes } from '../../crypto/base64url';
import { prepareShareForFamily } from '../../components/prepareShareForFamily';
import { familyKeyAuthentic } from '../../pkastore/familyKeyAuth';
import { uploadAttachment, deleteAttachmentRef } from '../../lib/attachments';
import DocumentLinksField from '../../components/DocumentLinksField';
import DocumentBodyEditor from '../../components/DocumentBodyEditor';
import LinkedItemsField from '../../components/LinkedItemsField';
import { HbsInput } from '../../components/hbs/Input';
import { HbsButton } from '../../components/hbs/Button';

const MAX_PHOTOS = 10;
const MAX_PHOTO_BYTES = 10 * 1024 * 1024;

/**
 * Create a family document (nav-decisions: documents are family-first — born in
 * a category, not personal). The document is written directly as a shared
 * `ORG#` item via the same `share-item-v1` ECIES path as a shared login/contact:
 * the `categoryId` rides inside the encrypted item and the category's `circleId`
 * is denormalized onto the envelope (so the server's circle read-filter
 * applies). Any member the server permits for that category's circle may create
 * one; there is no personal original.
 */
export default function DocumentEditorPage(): JSX.Element {
  const { orgId, categoryId, itemId } = useParams<{ orgId: string; categoryId: string; itemId?: string }>();
  const navigate = useNavigate();
  const { orgs, sync, updateItem, items } = useSafe();
  const categoriesByOrg = useFamilyCategories();

  const org = orgs.find((o) => o.id === orgId);
  const category = (orgId ? categoriesByOrg.get(orgId) ?? [] : []).find((c) => c.categoryId === categoryId);

  const existing = itemId
    ? items.find((i) => i.id === itemId && i.orgId === orgId && i.type === 'document')
    : undefined;
  const isEdit = !!existing;

  const [title, setTitle] = useState(existing?.name ?? '');
  const [body, setBody] = useState(existing?.body ?? '');
  const [links, setLinks] = useState<DocumentLink[]>(existing?.links ?? []);
  const [relatedItemIds, setRelatedItemIds] = useState<string[]>(existing?.relatedItemIds ?? []);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Existing attachments (edit mode) + newly picked files pending upload.
  const [existingPhotos, setExistingPhotos] = useState<AttachmentRef[]>(existing?.attachments ?? []);
  const [newFiles, setNewFiles] = useState<File[]>([]);
  const [removedPhotos, setRemovedPhotos] = useState<AttachmentRef[]>([]);

  const backTo = `/orgs/${orgId}/binder/${categoryId}`;

  function addFiles(files: FileList | null): void {
    if (!files) return;
    setError('');
    const picked = Array.from(files);
    const total = existingPhotos.length + newFiles.length + picked.length;
    for (const f of picked) {
      if (!f.type.startsWith('image/')) { setError(`"${f.name}" isn't an image — only photos can be added to your safe.`); return; }
      if (f.size > MAX_PHOTO_BYTES) { setError(`"${f.name}" is too large. Photos must be under 10 MB.`); return; }
    }
    if (total > MAX_PHOTOS) { setError(`A document can hold up to ${MAX_PHOTOS} photos.`); return; }
    setNewFiles((prev) => [...prev, ...picked]);
  }

  function removeExisting(ref: AttachmentRef): void {
    setExistingPhotos((prev) => prev.filter((p) => p.attachmentId !== ref.attachmentId));
    setRemovedPhotos((prev) => [...prev, ref]);
  }

  function removeNew(idx: number): void {
    setNewFiles((prev) => prev.filter((_, i) => i !== idx));
  }

  async function handleSubmit(e: React.FormEvent): Promise<void> {
    e.preventDefault();
    if (!title.trim() || !orgId || !categoryId) return;
    if (!org?.publicKey) {
      setError('Family is missing its public key. Try refreshing.');
      return;
    }
    // PKA (A2): authenticate the family public key before wrapping anything
    // (attachments included) to it — a malicious server could have substituted
    // org.publicKey. An unsigned family is rejected (no grandfathering).
    if (!familyKeyAuthentic(org)) {
      setError("Could not verify this family's security key — do not save. Ask a family admin to sign in to the latest version.");
      return;
    }
    setLoading(true);
    setError('');
    try {
      const cleanedLinks = links
        .filter((l) => l.url.trim())
        .map((l) => ({ url: l.url.trim(), ...(l.label?.trim() ? { label: l.label.trim() } : {}) }));

      // Fixed item id up front — the attachment S3 key needs it before upload.
      const docId = existing?.id ?? uuidv4();
      const pubKey = base64UrlToBytes(org.publicKey);

      // Upload newly-picked photos; a failure aborts the save with its message.
      const uploaded: AttachmentRef[] = [];
      for (const file of newFiles) {
        uploaded.push(await uploadAttachment(file, { orgId, itemId: docId, familyPublicKey: pubKey }));
      }
      const attachments = [...existingPhotos, ...uploaded];

      const fields = {
        name: title.trim(),
        body: body.trim() || undefined,
        links: cleanedLinks.length ? cleanedLinks : undefined,
        relatedItemIds: relatedItemIds.length ? relatedItemIds : undefined,
        attachments: attachments.length ? attachments : undefined,
      };

      if (isEdit && existing) {
        // updateItem preserves id + createdAt, bumps updatedAt, and the sync path
        // re-encrypts the shared item under the same id (server sees an update).
        await updateItem(existing.id, { type: 'document', ...fields });
        await sync();
        // Best-effort reap of removed photos (backstopped by the delete sweep).
        await Promise.all(removedPhotos.map((r) => deleteAttachmentRef(r, { orgId, itemId: existing.id })));
        navigate(`/orgs/${orgId}/binder/${categoryId}/documents/${existing.id}`);
        return;
      }

      const doc: VaultItem = {
        id: docId, type: 'document', ...fields, categoryId,
        sensitive: false, favorite: false, tags: [],
        createdAt: '', updatedAt: '',
      };
      const encrypted = await prepareShareForFamily(doc, pubKey, null, orgId, { itemId: docId });
      const itemToShare = category?.circleId ? { ...encrypted, circleId: category.circleId } : encrypted;
      const result = await shareItemWithFamily(itemToShare);
      if (!result.ok) {
        setError('Could not save the document. Please try again.');
        return;
      }
      await sync();
      navigate(backTo);
    } catch (err) {
      console.error('DocumentEditorPage: create failed', err);
      setError('Could not save the document. Please try again.');
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="h-full flex flex-col max-w-3xl">
      <form id="document-editor-form" onSubmit={handleSubmit} className="flex-1 min-h-0 overflow-y-auto p-7 flex flex-col gap-4">
        <div className="mb-2">
          <p className="text-[12px] font-bold uppercase tracking-hbs-overline text-hbs-text-mute">
            {org?.name ?? 'Family'}{category ? ` · ${category.name}` : ''}
          </p>
          <h1 className="font-display font-semibold text-[24px] tracking-hbs-tight text-hbs-text">
            {isEdit ? 'Edit document' : 'New document'}
          </h1>
        </div>

        <HbsInput label="Title" value={title} onChange={setTitle} placeholder="e.g. How to change the furnace filter" required autoFocus name="title" />

        <div>
          <label className="block text-[11.5px] font-bold uppercase tracking-hbs-overline text-hbs-text-mute mb-1">
            Body
          </label>
          <DocumentBodyEditor value={body} onChange={setBody} />
        </div>

        <DocumentLinksField values={links} onChange={setLinks} />

        <div>
          <label htmlFor="doc-photos" className="block text-[11.5px] font-bold uppercase tracking-hbs-overline text-hbs-text-mute mb-1">
            Photos
          </label>
          <input
            id="doc-photos"
            data-testid="photo-input"
            type="file"
            accept="image/*"
            multiple
            onChange={(e) => { addFiles(e.target.files); e.target.value = ''; }}
            className="block text-[13px] text-hbs-text"
          />
          {(existingPhotos.length > 0 || newFiles.length > 0) && (
            <ul className="mt-2 flex flex-wrap gap-2">
              {existingPhotos.map((p) => (
                <li key={p.attachmentId} className="flex items-center gap-1 px-2 py-1 bg-hbs-surface border border-hbs-border rounded-hbs-sm text-[12px]">
                  <span className="max-w-[140px] truncate">{p.filename}</span>
                  <button type="button" aria-label={`Remove ${p.filename}`} id={`document-remove-existing-${p.filename}`}
                  onClick={() => removeExisting(p)} className="text-hbs-text-mute hover:text-hbs-warning">×</button>
                </li>
              ))}
              {newFiles.map((f, i) => (
                <li key={`new-${i}`} className="flex items-center gap-1 px-2 py-1 bg-hbs-surface border border-hbs-border rounded-hbs-sm text-[12px]">
                  <span className="max-w-[140px] truncate">{f.name}</span>
                  <button type="button" aria-label={`Remove ${f.name}`} id={`document-remove-new-${i}`}
                  onClick={() => removeNew(i)} className="text-hbs-text-mute hover:text-hbs-warning">×</button>
                </li>
              ))}
            </ul>
          )}
        </div>

        <LinkedItemsField orgId={orgId!} selfItemId={itemId} value={relatedItemIds} onChange={setRelatedItemIds} />

        {error && <p role="alert" className="text-[12px] text-hbs-warning">{error}</p>}
      </form>
      <div className="shrink-0 border-t border-hbs-border bg-hbs-bg px-7 py-4 flex gap-2">
        <HbsButton id="document-save-button" type="submit" form="document-editor-form" loading={loading} fullWidth>Save</HbsButton>
        <HbsButton type="button" variant="ghost" fullWidth id="document-cancel-button" onClick={() => navigate(backTo)}>Cancel</HbsButton>
      </div>
    </div>
  );
}
